// V5 → V7 design migration. Legacy designs are saved with version "5.3.0".
//
// Empirical finding (verified headlessly): Fabric v7's loadFromJSON already loads
// v5-style JSON — its classRegistry resolves the lowercase v5 type aliases
// ('group', 'i-text', 'rect', …) and custom props survive. So migration is mostly
// NORMALIZATION + locking group geometry, not mandatory type remapping (map §4.4
// was more pessimistic than reality).
//
// What this does:
//   - marks the JSON as v7 (cosmetic `version`),
//   - gives every group a layoutManager so v7 doesn't re-fit and shift barcode/QR/SVG
//     groups (the map's StaticLayout concern) — uses the 'fixed' strategy to lock
//     the stored geometry,
//   - leaves types and custom props (barcodeFormat/qrValue/…) untouched (v7 reads them).

export interface DesignJSON {
  version?: string
  objects?: any[]
  [k: string]: unknown
}

/** True if the JSON is not already a v7 document. */
export function needsMigration(json: DesignJSON | null | undefined): boolean {
  return !!json && typeof json === 'object' && !String(json.version ?? '').startsWith('7')
}

function migrateObject(o: any): any {
  if (!o || typeof o !== 'object') return o
  const obj = { ...o }
  const type = String(obj.type ?? '').toLowerCase()
  if (type === 'group') {
    // Lock geometry so the loaded group renders exactly as stored (no relayout).
    if (!obj.layoutManager) obj.layoutManager = { type: 'layoutManager', strategy: 'fixed' }
    if (Array.isArray(obj.objects)) obj.objects = obj.objects.map(migrateObject)
  }
  return obj
}

/** Return a v7-compatible copy of a saved design's canvas_json. Idempotent for v7 input. */
export function migrateDesignJSON<T extends DesignJSON>(json: T): T {
  if (!json || typeof json !== 'object' || !needsMigration(json)) return json
  const out: DesignJSON = { ...json, version: '7.4.0' }
  if (Array.isArray(out.objects)) out.objects = out.objects.map(migrateObject)
  return out as T
}
